import apiV2 from "api-v2";
import type {
	AuditLogFilters,
	AuditLogResponse,
	CloneRolePayload,
	CreateRolePayload,
	ModuleResource,
	RenameRolePayload,
	RoleMatrixResource,
	RoleResource,
	UpdateRoleMatrixPayload,
	UserEffectivePermissions,
	AccessContactPage,
	AccessContactResource,
} from "../types/RolesPermissions.type";

// Endpoint paths verified against work/PRO-413/main/api/routes/api/v2/rbac.php
// and TASK_SUMMARY-011.md's contract table.

// FF-W3-3b: apiV2 has NO unwrap response interceptor (api-v2.js only sets the X-XSRF header and
// reads the session-expires header), so Laravel API-Resource responses arrive as the raw
// `{ data: ... }` envelope. These fetchers are typed as the INNER value, so the envelope must be
// unwrapped here to align runtime with the declared types (a wrapped object reaching the shared
// roles-list cache would crash the wizard's `roles.map`). Tolerant of an already-unwrapped body.
const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getRoles = async (): Promise<RoleResource[]> => {
	const response = await apiV2.get<{ data: RoleResource[] } | RoleResource[]>("/rbac/roles");

	return unwrap<RoleResource[]>(response.data);
};

export const getModules = async (): Promise<ModuleResource[]> => {
	const response = await apiV2.get<{ data: ModuleResource[] } | ModuleResource[]>("/rbac/modules");

	return unwrap<ModuleResource[]>(response.data);
};

export const getRoleMatrix = async (roleId: number): Promise<RoleMatrixResource> => {
	const response = await apiV2.get<{ data: RoleMatrixResource } | RoleMatrixResource>(
		`/rbac/roles/${roleId}/matrix`
	);

	return unwrap<RoleMatrixResource>(response.data);
};

export const updateRoleMatrix = async (
	roleId: number,
	payload: UpdateRoleMatrixPayload
): Promise<RoleMatrixResource> => {
	const response = await apiV2.put<{ data: RoleMatrixResource } | RoleMatrixResource>(
		`/rbac/roles/${roleId}/matrix`,
		payload
	);

	return unwrap<RoleMatrixResource>(response.data);
};

export const cloneRole = async (payload: CloneRolePayload): Promise<RoleResource> => {
	const response = await apiV2.post<{ data: RoleResource } | RoleResource>("/rbac/roles/clone", payload);

	return unwrap<RoleResource>(response.data);
};

export const createRole = async (payload: CreateRolePayload): Promise<RoleResource> => {
	const response = await apiV2.post<{ data: RoleResource } | RoleResource>("/rbac/roles", payload);

	return unwrap<RoleResource>(response.data);
};

export const renameRole = async (roleId: number, payload: RenameRolePayload): Promise<RoleResource> => {
	const response = await apiV2.patch<{ data: RoleResource } | RoleResource>(
		`/rbac/roles/${roleId}`,
		payload
	);

	return unwrap<RoleResource>(response.data);
};

export const deleteRole = async (roleId: number): Promise<void> => {
	await apiV2.delete(`/rbac/roles/${roleId}`);
};

// UM "view access" (PRO-413 #UM1) — read ANOTHER user's effective standing
// permissions for the read-only access matrix. Server-authoritative: the FE
// renders from `modules[key].can.*` and never derives access itself.
export const getUserEffectivePermissions = async (
	userId: number
): Promise<UserEffectivePermissions> => {
	const response = await apiV2.get<{ data: UserEffectivePermissions } | UserEffectivePermissions>(
		`/rbac/users/${userId}/permissions`
	);

	return unwrap<UserEffectivePermissions>(response.data);
};

// PRO-709 — the CURRENT user's own effective standing permissions. Distinct
// endpoint from the one above: `/rbac/me/permissions` carries no `can:` gate
// (a user may always read their own standing), whereas `/rbac/users/{id}/permissions`
// is admin-only. Feature surfaces gate on this; they must never re-derive access
// from role names or legacy flags such as `company_admin`.
export const getMyEffectivePermissions = async (): Promise<UserEffectivePermissions> => {
	const response = await apiV2.get<{ data: UserEffectivePermissions } | UserEffectivePermissions>(
		"/rbac/me/permissions"
	);

	return unwrap<UserEffectivePermissions>(response.data);
};

const auditFiltersToParams = (
	filters: AuditLogFilters
): Record<string, string | number> => {
	const params: Record<string, string | number> = {};

	if (filters.action_category) {
		params.action_category = filters.action_category;
	}
	if (filters.actor) {
		params.actor = filters.actor;
	}
	if (filters.context_type !== undefined) {
		params.context_type = filters.context_type;
	}
	if (filters.from) {
		params.from = filters.from;
	}
	if (filters.to) {
		params.to = filters.to;
	}

	return params;
};

export const getAuditLog = async (filters: AuditLogFilters): Promise<AuditLogResponse> => {
	const params: Record<string, string | number> = {
		...auditFiltersToParams(filters),
	};

	if (filters.page) {
		params.page = filters.page;
	}
	if (filters.per_page) {
		params.per_page = filters.per_page;
	}

	const response = await apiV2.get<AuditLogResponse>("/rbac/audit", { params });

	return response.data;
};

/**
 * Builds the absolute CSV export URL for /rbac/audit/export with the given
 * filters as query params. The caller must NOT fetch this via axios/fetch —
 * it is intended for a direct anchor/window.location navigation so the
 * browser handles the download via the backend's Content-Disposition header.
 * Export streams everything matching the filters (page/per_page are not
 * applicable here).
 */
export const buildAuditExportUrl = (filters: Omit<AuditLogFilters, "page" | "per_page">): string => {
	const baseUrl = apiV2.defaults.baseURL ?? "";
	const params = auditFiltersToParams(filters);
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		searchParams.set(key, String(value));
	});

	const queryString = searchParams.toString();
	const path = `${baseUrl}/rbac/audit/export`;

	return queryString ? `${path}?${queryString}` : path;
};

/**
 * PRO-737 — GET /rbac/me/access-contacts: the System Admins and Leads in the
 * caller's own company, to contact about getting permissions.
 *
 * Unlike the fetchers above this keeps the envelope, because the pagination
 * `meta` IS the payload here — the table pages five at a time. Tenancy is
 * forced server-side from the caller's company; there is no company parameter
 * to pass, by design.
 */
export const getAccessContacts = async (
	params: { search?: string; page?: number } = {}
): Promise<AccessContactPage> => {
	const response = await apiV2.get<{
		data: AccessContactResource[];
		meta?: { current_page?: number; last_page?: number; total?: number };
	}>("/rbac/me/access-contacts", {
		params: {
			...(params.search ? { search: params.search } : {}),
			...(params.page && params.page > 1 ? { page: params.page } : {}),
		},
	});

	const body = response.data;

	return {
		data: body?.data ?? [],
		currentPage: body?.meta?.current_page ?? 1,
		lastPage: body?.meta?.last_page ?? 1,
		total: body?.meta?.total ?? 0,
	};
};
